import React, { Fragment } from 'react';
import { get, keyBy, orderBy, sumBy, flatten, } from 'lodash';
import { formatISO, format as formatDate, addMonths, startOfMonth, endOfMonth, } from 'date-fns';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useToggle } from 'react-use';

import { yen, } from '../../util';
import firebase from '../../firebase';
import { paymentSchedules as _paymentSchedules, paymentScheduleFields } from '../../shared/models/tenant';
import { computePaymentFee } from '../../shared/models/sale';
import { firstShippedOrders } from '../../shared/models/order';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import ModelFormModal from '../modals/ModelFormModal';
import EditButton from '../EditButton';
import ExportButton from '../ExportButton';
import PdfDownloadButton from '../PdfDownloadButton';
import Page from '../hocs/Page';
import SalesTabs from '../SalesTabs';

const db = firebase.firestore();
const ordersRef = db.collection('orders');
const { max } = Math;

export default Page(function Sales (props) {
  const { tenant } = props;

  return (
    <div className="sales container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>売上管理</h4>
        </div>
        <div className="d-flex justify-content-between align-items-end mb-2">
          <EditButton
          label="早期入金オプション設定"
          itemRef={tenant.ref}
          FormModal={ModelFormModal}
          formProps={{
            title: '早期入金オプション設定',
            renderBodyHeader: _ => (
              <div className="mb-4 alert alert-info">
                当月の15日までに設定すると当月分の売上から適用されます。
                <br />
                16日以降に設定すると翌月分の売上から適用されます。
              </div>
            ),
            fields: {
              paymentSchedule: {
                ...paymentScheduleFields().paymentSchedule,
                hint: (
                  <div>
                    <div>
                      <span className="mr-1">※</span>
                      手数料率は合計出金額に対する手数料率になります。
                    </div>
                    <div className="d-flex">
                      <span className="mr-1">※</span>
                      入金日が土日祝の場合はその前日に入金されます。
                    </div>
                  </div>
                ),
              },
            },
          }}
        />
        </div>
        <div className="mt-4">
          <SalesTable tenant={tenant} />
        </div>
      </div>
    </div>
  );
});

export function SalesTable(props) {
  const { tenant } = props;
  const now = new Date();
  const sales = useCollectionSubscription(tenant.ref.collection('sales'), [tenant.id]);
  const paymentSchedules = useCollectionSubscription(tenant.ref.collection('paymentSchedules'), [tenant.id]);
  const paymentSchedulesById = keyBy(paymentSchedules, 'id');
  const invoices = useCollectionSubscription(tenant.ref.collection('invoices'), [tenant.id]);
  const sortedSales = orderBy(sales, _ => _.targetMonth.toDate(), 'desc');
  const twoMonthsOrders = useCollectionSubscription(ordersRef.where('tenantId', '==', tenant.id).where('createdAt', '>=', startOfMonth(addMonths(now, -1))), [tenant]);
  // 過去の月で売上計上済みの注文は当月に含めない（Amazonが後から出荷日を書き換えるため）
  const filteredOrders = firstShippedOrders(twoMonthsOrders, startOfMonth(now), null, formatDate(now, 'yyyy-MM'));
  const currentMonthRewards = useCollectionSubscription(tenant.ref.collection('rewards').where('createdAt', '>=', startOfMonth(now)), [tenant]);
  const currentMonthSale = {
    isCurrentMonth: true,
    id: formatDate(now, 'yyyy-MM'),
    amount: sumBy(filteredOrders.map(_ => _.items || []).flat(), _ => _.netPrice * _.quantity),
    count: filteredOrders.length,
    rewardsAmount: sumBy(currentMonthRewards, 'amount'),
    rewardsCount: currentMonthRewards.length,
    targetMonth: { toDate: _ => now },
  };

  return (
    <table className="table">
      <thead className="thead-light text-center">
        <tr className="text-nowrap">
          <th>売上月</th>
          <th>受取金額</th>
          <th>売上件数</th>
          <th>報酬金額</th>
          <th>報酬件数</th>
          <th>小計</th>
          <th>請求金額</th>
          <th>合計金額</th>
          <th>手数料</th>
          <th>入金額</th>
          <th>入金日</th>
          <th>ダウンロード</th>
        </tr>
      </thead>
      <tbody>
        {
          [currentMonthSale, ...sortedSales].map((sale) => {
            const { isCurrentMonth, id, amount, count, rewardsAmount = 0, rewardsCount = 0, targetMonth, shouldDeposit = false, } = sale;
            const paymentScheduleValue = get(paymentSchedulesById, [id, 'paymentSchedule'], (isCurrentMonth ? (tenant.paymentSchedule || 'end') : 'end'));
            const paymentSchedule = _paymentSchedules[paymentScheduleValue];
            const subtotalAmount = amount + rewardsAmount;
            const monthInvoices = invoices.filter(_ => formatDate(_.date.toDate(), 'yyyyMM') === formatDate(targetMonth.toDate(), 'yyyyMM'));
            const invoicesAmount = sale.invoicesAmount ?? sumBy(monthInvoices, 'amount');
            const monthTotalAmount = subtotalAmount - invoicesAmount;
            const totalAmount = sale.totalAmount ?? monthTotalAmount;
            const paymentScheduleFee = Math.floor(totalAmount * paymentSchedule.feeRate / 100);
            const paymentFee = sale.paymentFee != null ? sale.paymentFee : computePaymentFee(totalAmount - paymentScheduleFee);
            const totalFee = paymentScheduleFee + paymentFee;
            const conclusiveAmount = totalAmount <= 0 ? totalAmount : max(0, totalAmount - totalFee);

            return (
              <tr key={id}>
                <td>
                  {formatDate(targetMonth.toDate(), 'yyyy/MM')}
                  {shouldDeposit && <span className="ml-1 badge badge-info">デポジット</span>}
                </td>
                <td className="text-right">
                  {yen(amount)}
                </td>
                <td className="text-right">
                  {numeral(count).format('0,0')}
                </td>
                <td className="text-right">
                  {yen(rewardsAmount)}
                </td>
                <td className="text-right">
                  {numeral(rewardsCount).format('0,0')}
                </td>
                <td className="text-right font-weight-bold">
                  {yen(subtotalAmount)}
                </td>
                <td className="text-right">
                  <InvoicesAmountDisplay invoices={monthInvoices} />
                </td>
                <td className="text-right font-weight-bold">
                  {yen(monthTotalAmount)}
                </td>
                <td className="text-right">
                  {!shouldDeposit && !isCurrentMonth && yen(totalFee)}
                </td>
                <td className="text-right font-weight-bold">
                  {!shouldDeposit && !isCurrentMonth && yen(conclusiveAmount)}
                </td>
                <td>
                  {!shouldDeposit && !isCurrentMonth && formatDate(addMonths(targetMonth.toDate(), 1), 'yyyy年MM月')}{!shouldDeposit && !isCurrentMonth && paymentSchedule.dateLabel}
                </td>
                <td className="text-nowrap">
                  {!isCurrentMonth && (
                    <DownloadButtons
                      tenant={tenant}
                      sale={{
                        ...sale,
                        subtotalAmount,
                        invoicesAmount,
                        monthTotalAmount,
                        totalAmount,
                        totalFee,
                        conclusiveAmount,
                      }}
                      paymentScheduleDateLabel={paymentSchedule.dateLabel}
                    />
                  )}
                </td>
              </tr>
            );
          })
        }
      </tbody>
    </table>
  );
}

function DownloadButtons(props) {
  const { tenant, sale, paymentScheduleDateLabel } = props;
  const targetMonth = sale.targetMonth.toDate();
  const ymLabel = formatDate(targetMonth, 'yyyyMM');
  const tenantLabel = tenant.name || tenant.id;

  return (
    <div className="d-flex">
      <ExportButton
        size="sm"
        className="mr-1"
        fileName={`支払明細_${tenantLabel}_${ymLabel}.csv`}
        rows={() => fetchPaymentStatementRows(tenant, sale, paymentScheduleDateLabel)}
      >
        支払明細
      </ExportButton>
      <PdfDownloadButton
        size="sm"
        fileName={`支払精算書_${tenantLabel}_${ymLabel}.pdf`}
        html={() => buildPaymentStatementHtml({ tenant, sale, paymentScheduleDateLabel })}
        pdfOptions={{ printBackground: true }}
      >
        支払精算書
      </PdfDownloadButton>
    </div>
  );
}

async function fetchPaymentStatementRows(tenant, sale, paymentScheduleDateLabel) {
  const targetMonth = sale.targetMonth.toDate();
  const startOn = startOfMonth(targetMonth);
  const endOn = endOfMonth(targetMonth);
  const ordersSnap = await ordersRef
    .where('tenantId', '==', tenant.id)
    .where('createdAt', '>=', addMonths(startOn, -1))
    .where('createdAt', '<=', endOn)
    .get();
  const orders = ordersSnap.docs.map(_ => ({ id: _.id, ..._.data() }));
  const targetOrders = firstShippedOrders(orders, startOn, endOn, formatDate(targetMonth, 'yyyy-MM'));

  const orderRows = flatten(
    targetOrders.map((order) => {
      return (order.items || []).map((item) => {
        return {
          区分: '注文',
          注文番号: order.sourceOrderId || order.id,
          注文日: order.createdAt ? formatDate(order.createdAt.toDate(), 'yyyy/MM/dd') : '',
          出荷日: getShippedAtLabel(order),
          モール: order.mall || '',
          SKU: item.sku || '',
          単価: item.netPrice ?? '',
          数量: item.quantity ?? '',
          小計: (item.netPrice || 0) * (item.quantity || 0),
        };
      });
    })
  );

  const rewardsSnap = await tenant.ref.collection('rewards')
    .where('createdAt', '>=', startOn)
    .where('createdAt', '<=', endOn)
    .get();
  const rewardRows = rewardsSnap.docs.map((doc) => {
    const r = doc.data();
    return {
      区分: '報酬',
      注文番号: r.note || '',
      注文日: r.createdAt ? formatDate(r.createdAt.toDate(), 'yyyy/MM/dd') : '',
      出荷日: '',
      モール: '',
      SKU: '',
      単価: r.amount ?? '',
      数量: 1,
      小計: r.amount ?? 0,
    };
  });

  const invoicesSnap = await tenant.ref.collection('invoices').get();
  const invoiceRows = invoicesSnap.docs
    .map(_ => ({ id: _.id, ..._.data() }))
    .filter(_ => formatDate(_.date.toDate(), 'yyyyMM') === formatDate(targetMonth, 'yyyyMM'))
    .map((inv) => {
      return {
        区分: '請求',
        注文番号: inv.note || '',
        注文日: formatDate(inv.date.toDate(), 'yyyy/MM/dd'),
        出荷日: '',
        モール: '',
        SKU: '',
        単価: -(inv.amount || 0),
        数量: 1,
        小計: -(inv.amount || 0),
      };
    });

  const summaryRow = {
    区分: '合計',
    注文番号: `${formatDate(targetMonth, 'yyyy/MM')} 合計金額（手数料差引前）`,
    注文日: '',
    出荷日: '',
    モール: '',
    SKU: '',
    単価: '',
    数量: '',
    小計: sale.monthTotalAmount ?? 0,
  };

  return [...orderRows, ...rewardRows, ...invoiceRows, summaryRow];
}

function getShippedAtLabel(order) {
  const { inventoryType = 'fba' } = order;
  if (inventoryType === 'fba') {
    const shipments = flatten([get(order, 'FulfillmentShipment.member', [])]).filter(_ => _);
    const dates = shipments.map(_ => _.ShippingDateTime).filter(_ => _);
    return dates.map(_ => formatDate(new Date(_), 'yyyy/MM/dd')).join(',');
  }
  if (inventoryType === 'hanropluslogi') {
    const shippedOns = order.logispShippingRequest?.shippingItems?.map(_ => _.shippedOn).filter(_ => _) || [];
    return shippedOns.map(_ => formatDate(new Date(_), 'yyyy/MM/dd')).join(',');
  }
  return '';
}

function buildPaymentStatementHtml({ tenant, sale, paymentScheduleDateLabel }) {
  const targetMonth = sale.targetMonth.toDate();
  const targetMonthLabel = formatDate(targetMonth, 'yyyy年MM月');
  const payOnLabel = `${formatDate(addMonths(targetMonth, 1), 'yyyy年MM月')}${paymentScheduleDateLabel || ''}`;
  const issueDate = formatDate(new Date(), 'yyyy年MM月dd日');
  const fmtYen = (n) => `¥${numeral(n || 0).format('0,0')}`;
  const rows = [
    ['対象月', targetMonthLabel],
    ['受取金額', fmtYen(sale.amount)],
    ['報酬金額', fmtYen(sale.rewardsAmount)],
    ['小計', fmtYen(sale.subtotalAmount), true],
    ['請求金額', fmtYen(sale.invoicesAmount)],
    ['合計金額', fmtYen(sale.monthTotalAmount), true],
    ['手数料', fmtYen(sale.totalFee)],
    ['入金額', fmtYen(sale.conclusiveAmount), true],
    ['入金日', payOnLabel],
  ];
  const rowsHtml = rows.map(([label, value, bold]) => `
    <tr>
      <th style="background:#f3f4f6;border:1px solid #222;padding:8px;width:40%;text-align:left;">${label}</th>
      <td style="border:1px solid #222;padding:8px;text-align:right;${bold ? 'font-weight:bold;' : ''}">${value}</td>
    </tr>
  `).join('');

  return `<!DOCTYPE html>
<html lang="ja">
<head>
<meta charset="UTF-8" />
<title>支払精算書</title>
<style>
  body { font-family: 'Hiragino Sans', 'Hiragino Kaku Gothic ProN', 'Yu Gothic', 'Meiryo', sans-serif; color:#222; margin:40px 48px; font-size:11pt; }
  h1 { font-size:22pt; text-align:center; margin:0 0 24px; }
  .header { display:flex; justify-content:flex-end; font-size:10pt; margin-bottom:16px; }
  .two-col { display:flex; justify-content:space-between; gap:48px; margin-bottom:28px; }
  .recipient { flex:1; padding-top:8px; }
  .recipient .name { font-size:14pt; font-weight:bold; border-bottom:1px solid #222; padding-bottom:4px; margin-bottom:4px; }
  .recipient .honor { font-size:12pt; }
  .issuer { width:280px; }
  .issuer .name { font-size:12pt; font-weight:bold; margin-bottom:4px; }
  .issuer .line { font-size:9pt; margin-bottom:2px; }
  .greeting { margin-bottom:16px; line-height:1.6; }
  .total-box { background:#f3f4f6; border:1px solid #222; padding:12px; margin-bottom:24px; display:flex; justify-content:space-between; align-items:center; }
  .total-box .label { font-size:12pt; font-weight:bold; }
  .total-box .amount { font-size:20pt; font-weight:bold; }
  table { width:100%; border-collapse:collapse; }
</style>
</head>
<body>
  <div class="header">発行日: ${issueDate}</div>
  <h1>支払精算書</h1>
  <div class="two-col">
    <div class="recipient">
      <div class="name">${tenant.name || ''}</div>
      <div class="honor">御中</div>
    </div>
    <div class="issuer">
      <div class="name">株式会社ウィズリンクス</div>
      <div class="line">〒595-0811</div>
      <div class="line">大阪府泉北郡忠岡町忠岡北1-9-38</div>
      <div class="line">登録番号 T5120101053120</div>
    </div>
  </div>
  <div class="greeting">${targetMonthLabel}分のお支払いについて、下記のとおりご精算申し上げます。</div>
  <div class="total-box">
    <div class="label">お支払金額</div>
    <div class="amount">${fmtYen(sale.conclusiveAmount)}</div>
  </div>
  <table>${rowsHtml}</table>
</body>
</html>`;
}

function InvoicesAmountDisplay(props) {
  const { invoices } = props;
  const [showsModal, toggleModal] = useToggle(false);
  const onClick = (e) => {
    e.preventDefault();
    toggleModal(true);
  };

  return (
    <Fragment>
      <Link onClick={onClick}>
        {yen(sumBy(invoices, 'amount'))}
      </Link>
      {
        showsModal && (
          <Modal isOpen>
            <ModalHeader>
              請求内容確認
            </ModalHeader>
            <ModalBody>
              <table className="table">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th>発生日</th>
                    <th>金額</th>
                    <th>備考</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    invoices.map((invoice) => {
                      const { id, ref, date, amount, note, } = invoice;

                      return (
                        <tr key={id}>
                          <td>
                            {formatDate(date.toDate(), 'yyyy/MM/dd')}
                          </td>
                          <td className="text-right">
                            {numeral(amount).format()}
                          </td>
                          <td style={{ whiteSpace: 'pre-line' }}>
                            {note}
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </ModalBody>
            <ModalFooter>
              <Button className="cancel" color="secondary" onClick={toggleModal.bind(null, false)}>閉じる</Button>
            </ModalFooter>
          </Modal>
        )
      }
    </Fragment>
  );
}
